// Steps 3, 4 and 5 - purge, forwarding, and the Basic PI DNS write.
//
// The three share a shape (a decision, a stage, a per-domain result table) and
// therefore a file, exactly as the brief specified. What they do NOT share is
// weight: step 3 is irreversible on a zone we do not own, step 5 is the point of
// the whole tool, and step 4 only records intent.
//
// Two rules run through all three:
//
//   1. A badge is read off the per-domain `*_status` column and never off
//      `last_error`. A SUCCESSFUL basic stage legitimately leaves a
//      `zone_not_active` advisory in last_error; painting that row rose would
//      report a healthy domain as a failure.
//   2. `enqueued: false, alreadyRunning: true` means NOTHING was started. It is
//      reported as "already running", never as a fresh run.

import { useState } from 'react';

import {
  fdApi,
  fdErrorMessage,
  type FdDomain,
  type FdForwardingMechanism,
} from '../foreignDnsApi';
import {
  findStage,
  isZoneNotFound,
  summaryCount,
  useEscapeToClose,
  type FdStepProps,
} from './shared';

type Which = 'purge' | 'forwarding' | 'basic';

const FORWARDING_TEXTAREA_ID = 'fd-forwarding-text';

/**
 * Typed confirmation for the purge, matching the convention this codebase
 * already uses for every irreversible act (Domain Removal types DELETE, the
 * Serken cutover types CUTOVER).
 *
 * A recorded `purge_choice` plus a click-through modal was the whole consent
 * story, and this button is reachable from the shared-password `team` tier,
 * points at a THIRD PARTY's zone, and has neither an undo nor a backup. The
 * modal already names each zone and flags the ones wider than what was typed;
 * this makes the last click deliberate rather than reflexive.
 *
 * Frontend gate only, by design. The API keeps its two existing server-side
 * gates (route + processor, both on the recorded Yes) and gains no new field.
 */
const PURGE_CONFIRM_WORD = 'PURGE';

const PURGE_CONFIRM_ID = 'fd-purge-confirm';

/**
 * The exact record set the basic stage writes, in the operator's language.
 * `whenForwarding` rows are written only when step 4 answered Yes.
 *
 * !! MIRRORS THE BACKEND - CHANGE BOTH OR THIS SCREEN LIES !!
 *
 * Source of truth: `basicPiRecords` in src/lib/foreign-cf.ts. This table is what
 * step 5 promises the operator will be written, shown BEFORE they press Apply,
 * so a backend change that is not mirrored here is a false statement about a
 * customer's DNS on the one screen where they are deciding to go ahead.
 *
 * The duplication is forced, not lazy: Dockerfile.web builds the frontend in a
 * stage that copies only `frontend/`, so the repo-root src/ is not on disk and
 * that module cannot be imported here. Verified identical to the backend list at
 * the time of writing; nothing binds them but this comment and its twin.
 */
const BASIC_RECORDS: {
  type: string;
  name: string;
  content: string;
  ttl: string;
  note: string;
  whenForwarding?: true;
}[] = [
  { type: 'MX', name: '@', content: 'smtp.google.com', ttl: '3600', note: 'priority 1' },
  { type: 'TXT', name: '@', content: 'v=spf1 include:_spf.google.com ~all', ttl: '3600', note: '' },
  {
    type: 'TXT',
    name: '_dmarc',
    content: 'v=DMARC1; p=quarantine; pct=90; sp=none',
    ttl: '3600',
    note: '',
  },
  { type: 'CNAME', name: 'trk', content: 'open.sleadtrack.com', ttl: '3600', note: 'DNS only' },
  { type: 'CNAME', name: 'inst', content: 'prox.itrackly.com', ttl: '3600', note: 'DNS only' },
  {
    type: 'A',
    name: '@',
    content: '192.0.2.1',
    ttl: '1',
    note: 'proxied',
    whenForwarding: true,
  },
  {
    type: 'A',
    name: 'www',
    content: '192.0.2.1',
    ttl: '1',
    note: 'proxied',
    whenForwarding: true,
  },
];

function statusBadge(status: FdDomain['basic_status']): JSX.Element {
  // Straight off the status column. See rule 1 in the file header.
  return <span className={`fd-badge fd-badge--${status}`}>{status}</span>;
}

function mechanismChip(mechanism: FdForwardingMechanism | null): JSX.Element {
  if (mechanism === null) return <span className="fd-cell-faint">-</span>;
  if (mechanism === 'redirect_rules') {
    return <span className="fd-chip fd-chip--yes">Redirect rules</span>;
  }
  if (mechanism === 'page_rules') {
    return <span className="fd-chip fd-chip--warn">Page rules</span>;
  }
  return <span className="fd-chip fd-chip--no">None</span>;
}

/** last_error on a row that did NOT fail is an advisory, not a failure, so it is
 *  rendered as a faint note rather than in the error colour. */
function messageCell(status: FdDomain['basic_status'], message: string | null): JSX.Element {
  if (message === null) return <td className="fd-cell-faint">-</td>;
  return (
    <td className={status === 'failed' ? 'fd-cell-error' : 'fd-cell-faint'}>{message}</td>
  );
}

export function StepChoices(props: FdStepProps & { which: Which }): JSX.Element {
  const { which, ...rest } = props;
  if (which === 'purge') return <PurgeStep {...rest} />;
  if (which === 'forwarding') return <ForwardingStep {...rest} />;
  return <BasicStep {...rest} />;
}

/* ─── Step 3 - Purge ──────────────────────────────────────────────────────── */

/** One Cloudflare zone the purge stage will wipe, and the domains in this run
 *  that pulled it in. */
interface PurgeTarget {
  zoneId: string;
  zoneRoot: string;
  domains: string[];
  /**
   * No domain in this run IS the zone root - the operator typed only
   * subdomains, so wiping this zone destroys records at names they never named,
   * including whatever the customer runs at the apex. The confirm calls this
   * out explicitly.
   */
  wider: boolean;
}

/** Distinct zones, not domain rows. See the comment in PurgeStep. */
function purgeTargets(domains: FdDomain[]): PurgeTarget[] {
  const byZone = new Map<string, PurgeTarget>();
  for (const d of domains) {
    if (d.zone_id === null) continue;
    const found = byZone.get(d.zone_id);
    if (found) found.domains.push(d.domain);
    else {
      byZone.set(d.zone_id, {
        zoneId: d.zone_id,
        zoneRoot: d.zone_root,
        domains: [d.domain],
        wider: false,
      });
    }
  }
  const targets = [...byZone.values()];
  for (const t of targets) {
    t.wider = !t.domains.some((name) => name.toLowerCase() === t.zoneRoot.toLowerCase());
  }
  return targets;
}

function PurgeStep({
  runId,
  detail,
  reload,
  notifyEnqueued,
  goToStep,
  toast,
}: FdStepProps): JSX.Element {
  const { run, domains, stages } = detail;
  const purge = findStage(stages, 'purge');
  const running = purge?.status === 'running';

  // Purge is a ZONE-level operation - handlePurge calls client.purgeZone(zone_id)
  // - while the wizard's unit is a domain, and the two are not the same thing.
  // `mail.acme.com` is supported input and resolves to zone `acme.com`, so one
  // domain row can mean the customer's entire apex zone, website records and
  // all; and `acme.com` alongside it is still that same single zone. Counting
  // rows would have made the confirm say "1 zone" for the first case and
  // "2 zones" for the second, both wrong. Count distinct zone ids, and name the
  // roots so the operator sees what is actually going to be wiped.
  const zones = purgeTargets(domains);
  const zoneCount = zones.length;

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState('');
  const typedOk = typed.trim() === PURGE_CONFIRM_WORD;

  // Closing always clears the typed word, so re-opening the modal never starts
  // one click away from a wipe.
  function closeConfirm(): void {
    setConfirming(false);
    setTyped('');
  }

  useEscapeToClose(confirming, busy, closeConfirm);

  async function answerNo(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await fdApi.setChoices(runId, { purge_choice: 'no', current_step: 4 });
      await reload();
    } catch (err) {
      setError(fdErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function answerYes(): Promise<void> {
    // Belt to the disabled button: Enter in the confirm field must not be a
    // second, ungated route to the same irreversible call.
    if (!typedOk) return;
    setBusy(true);
    setError(null);
    try {
      // The consent has to be RECORDED before the stage is asked for: the route
      // answers 409 PURGE_NOT_CONSENTED on a run whose purge_choice is not
      // 'yes', and the processor re-checks it a second time before it decrypts
      // the key. Two gates, one recorded answer.
      await fdApi.setChoices(runId, { purge_choice: 'yes' });
      const res = await fdApi.runStage(runId, 'purge');
      closeConfirm();
      if (res.alreadyRunning) {
        toast('warn', 'The purge stage is already running - nothing new was started.');
      } else {
        notifyEnqueued();
        toast('success', `Purging ${zoneCount} zone(s)...`);
      }
      await reload();
    } catch (err) {
      closeConfirm();
      setError(fdErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const purged = summaryCount(purge, 'ok');
  const purgeFailed = summaryCount(purge, 'failed');

  return (
    <section className="fd-step">
      <div className="fd-step-head">
        <h2>Step 3 - Remove all existing DNS records?</h2>
        <p>
          Yes wipes every DNS record, every page rule and every dynamic redirect rule on each
          resolved zone, so the customer's domains start clean. No leaves their existing records
          alone and skips this stage entirely.
        </p>
      </div>
      <div className="fd-step-body">
        {run.purge_choice !== null && (
          <div className="fd-notice">
            Recorded answer: <strong>{run.purge_choice}</strong>.
          </div>
        )}

        <div className="fd-notice">
          {domains.length} domain(s) in this run resolve to <strong>{zoneCount}</strong> distinct
          Cloudflare zone(s). The purge works on zones, not on domains.
        </div>

        {/* Surfaced BEFORE the confirm, not only inside it: this is the case
            where a domain row is not the thing that gets wiped. */}
        {zones.some((z) => z.wider) && (
          <div className="fd-notice fd-notice--warn">
            <strong>Some zones are wider than the names you typed.</strong>{' '}
            {zones
              .filter((z) => z.wider)
              .map((z) => `${z.zoneRoot} (you typed ${z.domains.join(', ')})`)
              .join('; ')}
            . Purging removes every record on the whole zone, including anything the customer runs
            at the apex - a live website, for instance.
          </div>
        )}

        {error && <div className="fd-notice fd-notice--err">{error}</div>}

        {running && (
          <div className="fd-notice fd-notice--info">
            Purging {zoneCount} zone(s). Each one is re-read after the delete, because a 200 from a
            delete call is not proof the zone is empty. This page updates itself.
          </div>
        )}

        {!running && purge?.status === 'done' && (
          <div className="fd-notice">
            Purge finished
            {purged === null ? '' : `: ${purged} zone(s) cleared`}
            {purgeFailed === null || purgeFailed === 0 ? '' : `, ${purgeFailed} failed`}.
          </div>
        )}

        {!running && purge?.status === 'failed' && (
          <div className="fd-notice fd-notice--err">
            The purge stage failed: {purge.error_message ?? 'no message recorded'}
          </div>
        )}

        <div className="fd-actions">
          <button
            type="button"
            className="fd-btn fd-btn--danger"
            onClick={() => setConfirming(true)}
            disabled={busy || running || zoneCount === 0}
          >
            Yes, remove everything
          </button>
          <button
            type="button"
            className="fd-btn fd-btn--secondary"
            onClick={() => void answerNo()}
            disabled={busy || running}
          >
            No, leave their records
          </button>
          <div className="fd-actions-spacer" />
          <button
            type="button"
            className="fd-btn fd-btn--primary"
            onClick={() => void goToStep(4)}
            disabled={busy || running}
          >
            Continue to forwarding
          </button>
        </div>
      </div>

      {purge !== null && (
        <div className="fd-table-wrap">
          <table className="fd-table">
            <thead>
              <tr>
                <th className="fd-col-num">#</th>
                <th>Domain</th>
                <th>Zone wiped</th>
                <th className="fd-col-center">Purge</th>
                <th>Last message</th>
              </tr>
            </thead>
            <tbody>
              {domains.map((d) => (
                <tr key={d.id}>
                  <td className="fd-col-num fd-cell-faint">{d.position + 1}</td>
                  <td className="fd-cell-mono">{d.domain}</td>
                  <td className="fd-cell-mono fd-cell-faint">
                    {d.zone_id === null ? '-' : d.zone_root}
                  </td>
                  <td className="fd-col-center">{statusBadge(d.purge_status)}</td>
                  {messageCell(d.purge_status, d.last_error)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {purge !== null && (
        <div className="fd-step-body">
          <span className="fd-hint">
            <strong>Last message</strong> is one shared column per domain
            (<span className="fd-mono">last_error</span>), overwritten by whichever stage touched
            that domain most recently. After a later step runs, the text here can belong to that
            step rather than to the purge - the badge beside it is the purge's own verdict and is
            the authority.
          </span>
        </div>
      )}

      {confirming && (
        <div className="fd-modal-overlay">
          <div className="fd-modal">
            <div className="fd-modal-header">
              <h3 className="fd-modal-title">Remove all existing DNS?</h3>
            </div>
            <div className="fd-modal-body">
              <p>
                This deletes every DNS record, page rule and redirect rule on these {zoneCount}{' '}
                zones. It cannot be undone.
              </p>
              <p>
                The zones are the customer's own, and a zone is wiped in full - not just the names
                you typed:
              </p>
              <ul className="fd-modal-list">
                {zones.map((z) => (
                  <li key={z.zoneId}>
                    <span className="fd-mono">{z.zoneRoot}</span>
                    {z.wider ? (
                      <>
                        {' '}
                        <span className="fd-badge fd-badge--warn">whole zone</span>{' '}
                        <span className="fd-cell-faint">
                          you typed {z.domains.join(', ')} - everything else on{' '}
                          {z.zoneRoot} goes too
                        </span>
                      </>
                    ) : (
                      z.domains.length > 1 && (
                        <span className="fd-cell-faint"> from {z.domains.join(', ')}</span>
                      )
                    )}
                  </li>
                ))}
              </ul>
              <p>
                Domains in this run with no resolved Cloudflare zone are skipped and left untouched.
              </p>
              <div className="fd-field">
                <label className="fd-field-label" htmlFor={PURGE_CONFIRM_ID}>
                  Type {PURGE_CONFIRM_WORD} to confirm
                </label>
                <input
                  id={PURGE_CONFIRM_ID}
                  className="fd-input fd-mono"
                  type="text"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && typedOk && !busy) void answerYes();
                  }}
                  placeholder={`Type ${PURGE_CONFIRM_WORD}`}
                  autoComplete="off"
                  spellCheck={false}
                  disabled={busy}
                />
                <span className="fd-hint">
                  There is no undo and no backup. These zones belong to the customer.
                </span>
              </div>
            </div>
            <div className="fd-modal-footer">
              <button
                type="button"
                className="fd-btn fd-btn--danger"
                onClick={() => void answerYes()}
                disabled={busy || !typedOk}
                title={typedOk ? 'Wipe these zones' : `Type ${PURGE_CONFIRM_WORD} to enable this`}
              >
                {busy ? 'Starting...' : 'Yes, remove everything'}
              </button>
              <button
                type="button"
                className="fd-btn fd-btn--secondary"
                onClick={closeConfirm}
                disabled={busy}
              >
                Go back
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

/* ─── Step 4 - Forwarding ─────────────────────────────────────────────────── */

/**
 * No client-side preview of the normalised target, deliberately.
 *
 * The server's rules live in mapForwardingLines / normalizeForwardingUrl
 * (src/lib/foreign-cf-normalize.ts). The frontend cannot import them: the
 * production image builds the frontend in a stage that copies `frontend/` and
 * nothing else (Dockerfile.web), so a relative import into the backend's src/
 * would compile on a developer's machine and break the release build. Copying
 * the rules into TypeScript twice is the drift this project's Rule 7 exists to
 * stop, and a preview that drifts is worse than no preview: it would tell the
 * operator one thing and write another.
 *
 * So the mapping the operator sees is the mapping the server stored. Save is
 * cheap and non-destructive - setChoices only writes `forwarding_url` on the
 * domain rows, nothing reaches Cloudflare until step 5 - so "save and look" IS
 * the preview, it is re-runnable as many times as they like, and it cannot
 * disagree with what gets written because it IS what got written.
 */
function ForwardingStep({
  runId,
  detail,
  reload,
  goToStep,
  toast,
}: FdStepProps): JSX.Element {
  const { run, domains } = detail;

  const [text, setText] = useState(() => initialForwardingText(domains));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const chosen = run.forwarding_choice;
  const targeted = domains.filter((d) => d.forwarding_url !== null);
  const missingTargets = chosen === 'yes' && targeted.length < domains.length;

  async function answer(choice: 'yes' | 'no'): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await fdApi.setChoices(
        runId,
        choice === 'no' ? { forwarding_choice: 'no', current_step: 5 } : { forwarding_choice: 'yes' },
      );
      await reload();
    } catch (err) {
      setError(fdErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function saveTargets(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      // Sent together on purpose: the route validates the paste BEFORE it writes
      // anything, so a rejected list leaves neither the answer nor the targets
      // half-applied.
      await fdApi.setChoices(runId, { forwarding_choice: 'yes', forwarding_text: text });
      await reload();
      toast('success', 'Forwarding targets saved. The table below is what the server stored.');
    } catch (err) {
      setError(fdErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="fd-step">
      <div className="fd-step-head">
        <h2>Step 4 - Add forwarding?</h2>
        <p>
          Yes points each domain at a destination URL with a 301, written through Cloudflare
          Redirect Rules where the zone allows it and Page Rules where it does not. No skips
          forwarding, and then no A records are written at all in step 5. Where this run has
          already written forwarding for a domain, re-running step 5 also removes what it wrote
          there - and only what it wrote.
        </p>
      </div>
      <div className="fd-step-body">
        {chosen !== null && (
          <div className="fd-notice">
            Recorded answer: <strong>{chosen}</strong>.
          </div>
        )}

        {error && <div className="fd-notice fd-notice--err">{error}</div>}

        <div className="fd-actions">
          <button
            type="button"
            className="fd-btn fd-btn--primary"
            onClick={() => void answer('yes')}
            disabled={busy}
          >
            Yes, add forwarding
          </button>
          <button
            type="button"
            className="fd-btn fd-btn--secondary"
            onClick={() => void answer('no')}
            disabled={busy}
          >
            No forwarding
          </button>
        </div>

        {chosen === 'yes' && (
          <>
            <div className="fd-field">
              <label className="fd-field-label" htmlFor={FORWARDING_TEXTAREA_ID}>
                Forwarding targets
              </label>
              <textarea
                id={FORWARDING_TEXTAREA_ID}
                className="fd-textarea fd-mono"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={'https://acme.com'}
                spellCheck={false}
                disabled={busy}
              />
              <span className="fd-hint">
                Paste 1 URL to apply it to every domain, or one URL per line in the same order as
                your domains. This run has {domains.length} domain(s). Every target is stored as
                https and the path is kept exactly as typed. The server normalises each line and
                the table below shows what it actually stored - save as often as you like, nothing
                reaches Cloudflare until step 5.
              </span>
            </div>

            <div className="fd-actions">
              <button
                type="button"
                className="fd-btn fd-btn--primary"
                onClick={() => void saveTargets()}
                disabled={busy || text.trim() === ''}
              >
                {busy ? 'Saving...' : 'Save forwarding targets'}
              </button>
            </div>

            {missingTargets && (
              <div className="fd-notice fd-notice--warn">
                {domains.length - targeted.length} of {domains.length} domain(s) have no stored
                forwarding target. The Basic PI DNS step fails those rows with
                <span className="fd-mono"> forwarding_target_missing</span> rather than guessing.
                Save a target for every domain, or answer No to forwarding.
              </div>
            )}
          </>
        )}

        <div className="fd-actions">
          <div className="fd-actions-spacer" />
          <button
            type="button"
            className="fd-btn fd-btn--primary"
            onClick={() => void goToStep(5)}
            disabled={busy || chosen === null}
          >
            Continue to Basic PI DNS
          </button>
        </div>
      </div>

      {chosen === 'yes' && targeted.length > 0 && (
        <div className="fd-table-wrap">
          <table className="fd-table">
            <thead>
              <tr>
                <th className="fd-col-num">#</th>
                <th>Domain</th>
                <th>Stored target</th>
              </tr>
            </thead>
            <tbody>
              {domains.map((d) => (
                <tr key={d.id}>
                  <td className="fd-col-num fd-cell-faint">{d.position + 1}</td>
                  <td className="fd-cell-mono">{d.domain}</td>
                  {d.forwarding_url === null ? (
                    <td className="fd-cell-faint">no target stored</td>
                  ) : (
                    <td className="fd-cell-mono">{d.forwarding_url}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Re-fill the box from what the server holds, so a return visit shows the list
 *  that is actually stored. One URL on every domain collapses back to the single
 *  line that produced it. */
function initialForwardingText(domains: FdDomain[]): string {
  const urls = domains.map((d) => d.forwarding_url);
  if (urls.length === 0 || urls.some((u) => u === null)) return '';
  const stored = urls as string[];
  return new Set(stored).size === 1 ? stored[0] : stored.join('\n');
}

/* ─── Step 5 - Basic PI DNS ───────────────────────────────────────────────── */

function BasicStep({
  runId,
  detail,
  reload,
  notifyEnqueued,
  goToStep,
  toast,
}: FdStepProps): JSX.Element {
  const { run, domains, stages } = detail;
  const basic = findStage(stages, 'basic');
  const running = basic?.status === 'running';
  const withForwarding = run.forwarding_choice === 'yes';
  const records = BASIC_RECORDS.filter((r) => !r.whenForwarding || withForwarding);
  const subdomains = domains.filter((d) => d.record_prefix !== null);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function apply(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const res = await fdApi.runStage(runId, 'basic');
      if (res.alreadyRunning) {
        toast('warn', 'The Basic PI DNS stage is already running - nothing new was started.');
      } else {
        notifyEnqueued();
        toast('success', `Writing the basic record set to ${domains.length} domain(s)...`);
      }
      await reload();
    } catch (err) {
      setError(fdErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const scopeMissing = domains.some((d) => (d.last_error ?? '').includes('forwarding_scope_missing'));
  const usedPageRules = domains.some((d) => d.forwarding_mechanism === 'page_rules');
  const ok = summaryCount(basic, 'ok');
  const failed = summaryCount(basic, 'failed');
  const reachableDomains = domains.filter((d) => !isZoneNotFound(d));
  const reachable = reachableDomains.length;
  const settled =
    reachable > 0 &&
    reachableDomains.every((d) => d.basic_status === 'ok' || d.basic_status === 'skipped');

  return (
    <section className="fd-step">
      <div className="fd-step-head">
        <h2>Step 5 - Add all Basic PI DNS</h2>
        <p>
          Writes the standard Premium Inboxes record set to every resolved zone in this run. There
          is deliberately no No - this is the point of the tool. The stage is idempotent, so
          re-running it is always safe, and it turns green only after a fresh read-back from
          Cloudflare confirms every record.
        </p>
      </div>
      <div className="fd-step-body">
        <div className="fd-notice">
          Forwarding is <strong>{run.forwarding_choice ?? 'not answered'}</strong> for this run
          {withForwarding
            ? ', so the two proxied A records are included and a 301 redirect is applied per zone.'
            : ', so no A records are written and no redirect is applied.'}
        </div>

        {!withForwarding && (
          <div className="fd-notice">
            Where the <strong>Forwarding used</strong> column below shows that this run already
            wrote forwarding for a domain, this step also removes it: the A records on{' '}
            <span className="fd-mono">192.0.2.1</span> at that domain's apex and{' '}
            <span className="fd-mono">www</span>, its own redirect rule (matched on the description
            this tool writes), and its own page rules (matched on the forwarding target this run
            stored). Rows with no recorded mechanism are left completely alone - their rulesets and
            page rules are not even read. Anything the customer wrote is never touched, and
            anything at those names that could not be proved to be ours is reported instead of
            deleted.
          </div>
        )}

        {subdomains.length > 0 && (
          <div className="fd-notice">
            {subdomains.length} domain(s) in this run are subdomains, so their records are written
            under the prefix rather than at the apex - for example{' '}
            <span className="fd-mono">trk.{subdomains[0].record_prefix}</span> on{' '}
            <span className="fd-mono">{subdomains[0].zone_root}</span>.
          </div>
        )}

        {error && <div className="fd-notice fd-notice--err">{error}</div>}

        {running && (
          <div className="fd-notice fd-notice--info">
            Writing records to {domains.length} domain(s) and reading each zone back. This page
            updates itself.
          </div>
        )}

        {!running && basic?.status === 'done' && (
          <div className="fd-notice">
            Basic PI DNS finished
            {ok === null ? '' : `: ${ok} ok`}
            {failed === null || failed === 0 ? '' : `, ${failed} failed`}.
          </div>
        )}

        {!running && basic?.status === 'failed' && (
          <div className="fd-notice fd-notice--err">
            The Basic PI DNS stage failed: {basic.error_message ?? 'no message recorded'}
          </div>
        )}

        {scopeMissing && (
          <div className="fd-notice fd-notice--warn">
            <strong>Cloudflare token is missing the forwarding scope.</strong> One or more domains
            could not get a redirect through either Redirect Rules or Page Rules. Add{' '}
            <span className="fd-mono">Zone &gt; Dynamic Redirect &gt; Edit</span> to the customer's
            API token, then re-test the key and run this step again - or turn forwarding off. Every
            DNS record that did land is already in place; re-running only fixes the redirect.
          </div>
        )}

        {scopeMissing && (
          <div className="fd-actions">
            <button
              type="button"
              className="fd-btn fd-btn--secondary"
              onClick={() => void goToStep(1)}
              disabled={busy}
            >
              Re-test key
            </button>
            <button
              type="button"
              className="fd-btn fd-btn--secondary"
              onClick={() => void goToStep(4)}
              disabled={busy}
            >
              Turn forwarding off
            </button>
          </div>
        )}

        {usedPageRules && (
          <div className="fd-notice fd-notice--warn">
            One or more zones fell back to <strong>Page Rules</strong> because Redirect Rules were
            not available. Cloudflare's free plan caps page rules at 3 per zone and forwarding uses
            all 3, so those zones have no page-rule budget left for anything else.
          </div>
        )}

        <div className="fd-table-wrap">
          <table className="fd-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Name</th>
                <th>Content</th>
                <th className="fd-col-center">TTL</th>
                <th className="fd-col-center">Note</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r) => (
                <tr key={`${r.type}-${r.name}`}>
                  <td className="fd-cell-mono">{r.type}</td>
                  <td className="fd-cell-mono">{r.name}</td>
                  <td className="fd-cell-mono">{r.content}</td>
                  <td className="fd-col-center fd-cell-faint">{r.ttl}</td>
                  <td className="fd-col-center fd-cell-faint">{r.note || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {reachable === 0 && domains.length > 0 && (
          <div className="fd-notice fd-notice--warn">
            No domain in this run has a resolved Cloudflare zone, so this stage would skip every
            row. Go back to step 2 and re-check the domains first.
          </div>
        )}

        <div className="fd-actions">
          <button
            type="button"
            // Same guard as step 3's Yes and step 2's Continue: a stage that
            // would skip every row is not worth a job.
            className="fd-btn fd-btn--primary"
            onClick={() => void apply()}
            disabled={busy || running || reachable === 0}
          >
            {running ? 'Running...' : 'Apply Basic PI DNS'}
          </button>
          <div className="fd-actions-spacer" />
          {/* Gated on `settled` for the same reason the rail is: step 6 needs
              every reachable domain to have finished this one, and the route
              would let the pointer move anyway. */}
          <button
            type="button"
            className="fd-btn fd-btn--secondary"
            onClick={() => void goToStep(6)}
            disabled={busy || running || !settled}
            title={
              settled
                ? 'Go to the Google verification codes'
                : 'Some domains have not settled this step yet'
            }
          >
            Continue to verification
          </button>
        </div>

        {settled && (
          <div className="fd-notice">
            Every reachable domain has settled this step. Carry on to the Google verification codes.
          </div>
        )}
      </div>

      {basic !== null && (
        <div className="fd-table-wrap">
          <table className="fd-table">
            <thead>
              <tr>
                <th className="fd-col-num">#</th>
                <th>Domain</th>
                <th className="fd-col-center">Basic DNS</th>
                <th className="fd-col-center">Forwarding used</th>
                <th>Last message</th>
              </tr>
            </thead>
            <tbody>
              {domains.map((d) => (
                <tr key={d.id}>
                  <td className="fd-col-num fd-cell-faint">{d.position + 1}</td>
                  <td className="fd-cell-mono">{d.domain}</td>
                  <td className="fd-col-center">{statusBadge(d.basic_status)}</td>
                  <td className="fd-col-center">{mechanismChip(d.forwarding_mechanism)}</td>
                  {messageCell(d.basic_status, d.last_error)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {basic !== null && (
        <div className="fd-step-body">
          <span className="fd-hint">
            <strong>Last message</strong> is one shared column per domain
            (<span className="fd-mono">last_error</span>), overwritten by whichever stage touched
            that domain most recently, so once steps 6 and 7 exist the text here can belong to one
            of those. It is also not always a failure: a green row carrying{' '}
            <span className="fd-mono">zone_not_active</span> is a healthy write on a zone Cloudflare
            is not authoritative for yet. The badge is the verdict.
          </span>
        </div>
      )}
    </section>
  );
}
